import type { IsoDateTime } from '@omnipresentgroup/design-system';

import type { AddressAPI, Country } from '../../common';
import type {
  BillingCurrencyEnum,
  Contract,
  ContractOutstandingFee,
  Contractor,
  ContractorBankDetails,
  ContractorIdentityVerification,
  ContractorPersonalDetails,
  ContractorState,
  ContractState,
  EntityType,
  InvoicingCurrencyEnum,
  InvoicingFrequencyEnum,
  OnboardingStepState,
  OutstandingFeeStatusEnum,
  OutstandingFeeTypeEnum,
} from '../contractors';

/* API */

export enum LiabilityShieldStateAPI {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  AWAITING_ACTIVATION = 'AWAITING_ACTIVATION',
}

export type LiabilityShieldAPI = {
  state: LiabilityShieldStateAPI;
  start_date: string;
  end_date?: string;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
};

export type ContractAPI = {
  id: string;
  contractor_id: string;
  client_id: string;
  job_role: string;
  invoicing_currency: InvoicingCurrencyEnum;
  billing_currency: BillingCurrencyEnum;
  expected_invoicing_frequency?: InvoicingFrequencyEnum;
  rate?: string;
  start_date?: string;
  end_date?: string;
  state: ContractState;
  liability_shield_coverages?: LiabilityShieldAPI[];
};

export type ContractorAPI = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  residence_country: Country;
  state: ContractorState;
  contracts: ContractAPI[];
  user_id: string;
  onboarding_states: {
    kyc_state: OnboardingStepState;
    personal_state: OnboardingStepState;
    banking_account_state: OnboardingStepState;
  };
  misclassification_risk_accepted?: boolean;
  misclassification_risk_accepted_at?: string;
  misclassification_risk_accepted_by?: string;
};

export type GetContractorResponseAPI = ContractorAPI;

export type GetContractorsResponseAPI = {
  items: ContractorAPI[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type CreateContractorRequestAPI = {
  first_name: string;
  last_name: string;
  email: string;
  job_role: string;
  residence_country: string;
  invoicing_currency: InvoicingCurrencyEnum;
  billing_currency: BillingCurrencyEnum;
  expected_invoicing_frequency?: InvoicingFrequencyEnum;
  invoice_amount?: string;
  start_date?: string;
  end_date?: string;
  misclassification_risk_accepted: boolean;
  misclassification_assessment_code?: string;
  contract_template_usage_accepted?: boolean;
};

export type GetContractorsCostsRequestAPI = {
  billing_currency: BillingCurrencyEnum;
  invoicing_currency: InvoicingCurrencyEnum;
};

export type GetContractorsCostsResponseAPI = {
  monthly_platform_fee: string;
  total_cost: string;
  vat_rate?: string;
  country: Country;
};

export type GetCountriesResponseAPI = Country[];

export type ContractorPersonalDetailsAPI = {
  state: OnboardingStepState;
  state_last_error_message?: string;
  first_name?: string;
  last_name?: string;
  residence_country?: Country;
  nationality?: Country;
  date_of_birth?: string;
};

export type GetContractorPersonalDetailsResponseAPI =
  ContractorPersonalDetailsAPI;

export type UpdateContractorPersonalDetailsRequestAPI = {
  first_name: string;
  last_name: string;
  residence_country: string;
  nationality: string;
  date_of_birth: string;
};

export enum BankTypesEnumAPI {
  IBAN = 'IBAN',
  GB = 'GB',
  US = 'US',
  CA = 'CA',
  AU = 'AU',
  OTHER = 'OTHER',
}

export type ContractorBankDetailsAPI = {
  state: OnboardingStepState;
  state_last_error_message?: string;
  bank_account_type: BankTypesEnumAPI;
  owner_name: string;
  iban?: string;
  swift_bic?: string;
  aba?: string;
  institution_number?: string;
  branch_code?: string;
  bank_name?: string;
  sort_code?: string;
  country_code?: string;
  account_number?: string;
  bank_state_branch?: string;
  owner_address: AddressAPI;
  id?: string;
};

export type GetContractorBankDetailsResponseAPI = ContractorBankDetailsAPI;

export type UpdateContractorBankDetailsRequestAPI = ContractorBankDetailsAPI;

export type ContractorIdentityVerificationAPI = {
  state: OnboardingStepState;
  state_last_error_message?: string;
  entity_type: EntityType;
  terms_agreed?: boolean;
  terms_agreed_at?: string;
  entity_type_confirmed?: boolean;
  entity_type_confirmed_at?: string;
  registered_legal_name?: string;
  entity_email?: string;
  company_number?: string;
  headquarters_address?: {
    address_line_1: string;
    address_line_2?: string;
    city: string;
    postal_code: string;
    region?: string;
    country: Country;
  };
  legal_representative?: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    nationality: Country;
    country_of_residence: Country;
  };
};

export type GetContractorIdentityVerificationResponseAPI =
  ContractorIdentityVerificationAPI;

export type UpdateContractorIdentityVerificationRequestAPI = {
  entity_type: EntityType;
  terms_agreed: boolean;
  entity_type_confirmed: boolean;
  registered_legal_name?: string;
  entity_email?: string;
  company_number?: string;
  headquarters_address?: {
    address_line_1: string;
    address_line_2?: string;
    city: string;
    postal_code: string;
    region?: string;
    country: string;
  };
  legal_representative?: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    nationality: string;
    country_of_residence: string;
  };
};

export type GetContractorAllowedEntitiesResponseAPI = {
  allowed_kyc_entity_types: EntityType[];
};

export type ContractOutstandingFeeApi = {
  id: string;
  contract_id: string;
  type: OutstandingFeeTypeEnum;
  amount: string;
  vat_rate: string;
  currency: BillingCurrencyEnum;
  month: string;
  status: OutstandingFeeStatusEnum;
};

export type GetContractorContractOutstandingFeesResponseAPI =
  ContractOutstandingFeeApi[];

export type UpdateContractorContractRequestAPI = Pick<
  ContractAPI,
  'rate' | 'expected_invoicing_frequency' | 'start_date' | 'end_date'
>;

export type UpdateContractorContractResponseAPI =
  UpdateContractorContractRequestAPI;

/* Model */

export type GetContractorResponse = Contractor;

export type GetContractorsResponse = {
  items: Contractor[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type CreateContractorRequest = {
  firstName: string;
  lastName: string;
  email: string;
  jobRole: string;
  residenceCountry: string;
  invoicingCurrency: InvoicingCurrencyEnum;
  billingCurrency: BillingCurrencyEnum;
  expectedInvoicingFrequency?: InvoicingFrequencyEnum;
  invoiceAmount?: string;
  startDate?: string;
  endDate?: string;
  misclassificationRiskAccepted: boolean;
  misclassificationAssessmentCode?: string;
  contractTemplateUsageAccepted?: boolean;
};

export type GetContractorsCostsRequest = {
  billingCurrency: BillingCurrencyEnum;
  invoicingCurrency: InvoicingCurrencyEnum;
};

export type GetContractorsCostsResponse = {
  monthlyPlatformFee: string;
  totalCost: string;
  vatRate?: string;
  country: Country;
};

export type GetCountriesResponse = Country[];

export type GetContractorPersonalDetailsResponse = ContractorPersonalDetails;

export type UpdateContractorPersonalDetailsRequest = {
  firstName: string;
  lastName: string;
  residenceCountry: string;
  nationality: string;
  dateOfBirth: string;
};

export type GetContractorBankDetailsResponse = ContractorBankDetails;

export type UpdateContractorBankDetailsRequest = ContractorBankDetails;

export type GetContractorIdentityVerificationResponse =
  ContractorIdentityVerification;

export type UpdateContractorIdentityVerificationRequest = {
  entityType: EntityType;
  termsAgreed: boolean;
  entityTypeConfirmed: boolean;
  registeredLegalName?: string;
  entityEmail?: string;
  companyNumber?: string;
  headquartersAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postalCode: string;
    region?: string;
    country: string;
  };
  legalRepresentative?: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    nationality: string;
    countryOfResidence: string;
  };
};

export type GetContractorAllowedEntitiesResponse = {
  allowedKycEntityTypes: EntityType[];
};

export type GetContractorContractOutstandingFeesResponse =
  ContractOutstandingFee[];

export type UpdateContractorContractRequest = Pick<
  Contract,
  'rate' | 'expectedInvoicingFrequency' | 'startDate' | 'endDate'
>;

export type UpdateContractorContractResponse = UpdateContractorContractRequest;
